/**
 * VoxelCollision - CPU-side sparse voxel octree traversal for collision detection.
 *
 * Reads the Laine-Karras SVO format produced by @playcanvas/splat-transform writeVoxel().
 * All queries are pure Uint32 array math — no GPU, no PlayCanvas dependency.
 *
 * Binary layout (.voxel.bin):
 *   bytes [0, nodeCount*4)        : nodes Uint32Array (interior + marker nodes)
 *   bytes [nodeCount*4, end)      : leafData Uint32Array ([lo,hi] pairs for mixed leaves)
 *
 * Node encoding (each u32):
 *   bits 31-24  = childMask  (8 bits, one per octant 0-7)
 *   bits 23-0   = baseOffset (24 bits, index of first child in nodes array)
 *
 * SOLID_LEAF_MARKER = childMask=0xFF + baseOffset=0
 *   Fully solid subtree. Safe because BFS layout guarantees children after parent,
 *   so baseOffset=0 is never valid for an interior node.
 *
 * Mixed leaf: childMask=0x00, baseOffset = index into leafData / 2.
 *   leafData[baseOffset*2]   = lo (bits 0..31 for voxels 0..31)
 *   leafData[baseOffset*2+1] = hi (bits 32..63 for voxels 32..63)
 *
 * 4x4x4 block layout — linear within each leaf:
 *   bit index = lx + ly*4 + lz*16
 */

export interface PushOut {
    x: number;
    y: number;
    z: number;
}

export interface VoxelOctreeMetadata {
    version: string;
    gridBounds: { min: number[]; max: number[] };
    sceneBounds: { min: number[]; max: number[] };
    voxelResolution: number;
    leafSize: number;          // always 4
    treeDepth: number;
    numInteriorNodes: number;
    numMixedLeaves: number;
    nodeCount: number;
    leafDataCount: number;
}

// childMask=0xFF, baseOffset=0
const SOLID_LEAF_MARKER = (0xFF << 24) | 0;

/**
 * splat-transform ≥2 (voxel format 1.1+) writes octree data in "engine space",
 * which is file/PLY-local space rotated 180° about Z (x and y negated). The
 * gsplat renderer draws the raw file-local payload, and CharacterController /
 * CameraControls transform queries into file-local space — so for 1.1+ data
 * every public query on this class flips x/y on the way in and out. Format 1.0
 * data (splat-transform 1.x) is already file-local and passes through
 * untouched. This mirrors (inverted) the library's own legacy check in
 * loadVoxelCollision / FlippedVoxelCollision.
 */
function isEngineSpaceVoxelFormat(version: string | undefined): boolean {
    if (!version) return false;
    const parsed = parseFloat(version);
    return Number.isFinite(parsed) && parsed >= 1.1;
}

// Encode leaf-local (lx, ly, lz) each in [0,3] to a 6-bit linear index (0-63).
// Matches writeVoxel() layout: bitIdx = lx + ly*4 + lz*16
function leafBitIndex(lx: number, ly: number, lz: number): number {
    return lx + ly * 4 + lz * 16;
}

// Test a single voxel bit in leafData given pair index and bit index
function testLeafBit(leafData: Uint32Array, baseOffset: number, bitIdx: number): boolean {
    const pairIdx = baseOffset * 2;
    if (bitIdx < 32) {
        return ((leafData[pairIdx] >>> bitIdx) & 1) === 1;
    }
    return ((leafData[pairIdx + 1] >>> (bitIdx - 32)) & 1) === 1;
}

// Popcount for bits below a given octant in childMask
function childIndexBefore(childMask: number, octant: number): number {
    const lowBits = childMask & ((1 << octant) - 1);
    let bits = lowBits;
    let count = 0;
    while (bits) { count++; bits &= bits - 1; }
    return count;
}

export class VoxelCollision {
    private nodes: Uint32Array;
    private leafData: Uint32Array;
    private meta: VoxelOctreeMetadata;

    // Cached bounds for hot-path access
    private gridMinX: number;
    private gridMinY: number;
    private gridMinZ: number;
    private gridMaxX: number;
    private gridMaxY: number;
    private gridMaxZ: number;
    private voxelRes: number;
    private leafBlockSize: number; // leafSize * voxelRes (4 * res)
    private cellsPerAxis: number;  // leafSize * 2^treeDepth — octree extent in cells (power of 2)
    // True for voxel format 1.1+ (engine-space data): queries flip x/y between
    // the file-local boundary and the stored octree coords.
    private flipXY: boolean;

    constructor(nodes: Uint32Array, leafData: Uint32Array, meta: VoxelOctreeMetadata) {
        this.nodes = nodes;
        this.leafData = leafData;
        this.meta = meta;
        this.flipXY = isEngineSpaceVoxelFormat(meta.version);

        // The octree is padded to 2^treeDepth blocks per dimension, but gridBounds
        // in the metadata only stores the actual (unpadded) grid extent.
        // We must use the padded octree extent for traversal, since the node structure
        // was built with the padded bounds — using gridBounds would compress positions.
        const blockSize = meta.leafSize * meta.voxelResolution;
        const octreeExtent = Math.pow(2, meta.treeDepth) * blockSize;

        this.gridMinX = meta.gridBounds.min[0];
        this.gridMinY = meta.gridBounds.min[1];
        this.gridMinZ = meta.gridBounds.min[2];
        this.gridMaxX = this.gridMinX + octreeExtent;
        this.gridMaxY = this.gridMinY + octreeExtent;
        this.gridMaxZ = this.gridMinZ + octreeExtent;
        this.voxelRes = meta.voxelResolution;
        this.leafBlockSize = meta.leafSize * meta.voxelResolution;
        this.cellsPerAxis = meta.leafSize * Math.pow(2, meta.treeDepth);
    }

    /**
     * Load VoxelCollision from a .voxel.json URL.
     * The .voxel.bin is derived from the same base path.
     */
    static async load(jsonUrl: string): Promise<VoxelCollision> {
        console.log(`[VoxelCollision.load] Fetching JSON: ${jsonUrl.substring(0, 100)}...`);
        // Fetch JSON metadata first — it may contain an explicit binUrl
        // (Firebase Storage download URLs have per-file tokens, so simple
        //  string replacement doesn't work for authenticated URLs)
        const jsonResp = await fetch(jsonUrl);
        if (!jsonResp.ok) throw new Error(`[VoxelCollision.load] Failed to fetch .voxel.json: ${jsonResp.status} ${jsonResp.statusText}`);

        const meta: VoxelOctreeMetadata & { binUrl?: string } = await jsonResp.json();
        console.log(`[VoxelCollision.load] JSON parsed — nodeCount: ${meta.nodeCount}, leafDataCount: ${meta.leafDataCount}, res: ${meta.voxelResolution}, hasBinUrl: ${!!meta.binUrl}`);

        // Use embedded binUrl if present, otherwise derive from the JSON URL
        const binUrl = meta.binUrl ?? jsonUrl.replace(/\.voxel\.json(\?.*)?$/, (_match, qs) =>
            `.voxel.bin${qs ?? ''}`
        );
        console.log(`[VoxelCollision.load] Fetching BIN (${meta.binUrl ? 'embedded URL' : 'derived URL'}): ${binUrl.substring(0, 100)}...`);

        const binResp = await fetch(binUrl);
        if (!binResp.ok) throw new Error(`[VoxelCollision.load] Failed to fetch .voxel.bin: ${binResp.status} ${binResp.statusText} — URL: ${binUrl.substring(0, 120)}`);

        const binBuffer = await binResp.arrayBuffer();
        console.log(`[VoxelCollision.load] BIN fetched: ${binBuffer.byteLength} bytes`);

        const expectedBytes = (meta.nodeCount + meta.leafDataCount) * 4;
        if (binBuffer.byteLength < expectedBytes) {
            throw new Error(
                `[VoxelCollision.load] Binary truncated: expected ${expectedBytes} bytes, got ${binBuffer.byteLength}`
            );
        }

        const nodeByteCount = meta.nodeCount * 4;
        const nodes = new Uint32Array(binBuffer, 0, meta.nodeCount);
        const leafData = new Uint32Array(binBuffer, nodeByteCount, meta.leafDataCount);

        const blockSize = meta.leafSize * meta.voxelResolution;
        const octreeExtent = Math.pow(2, meta.treeDepth) * blockSize;
        console.log(`[VoxelCollision.load] OK — ${meta.nodeCount} nodes, ${meta.numMixedLeaves} mixed leaves, grid: [${meta.gridBounds.min}] → [${meta.gridBounds.max}], octreeBounds: [${meta.gridBounds.min[0]},${meta.gridBounds.min[1]},${meta.gridBounds.min[2]}] → [${meta.gridBounds.min[0] + octreeExtent},${meta.gridBounds.min[1] + octreeExtent},${meta.gridBounds.min[2] + octreeExtent}] (extent=${octreeExtent})`);
        if (isEngineSpaceVoxelFormat(meta.version)) {
            console.log(`[VoxelCollision.load] Format ${meta.version} (engine space) — queries will flip x/y to file-local space`);
        }
        return new VoxelCollision(nodes, leafData, meta);
    }

    /** Natural query resolution (file-local units) — the leaf voxel edge length. Lets the reticle
     *  raycast size its sweep so the per-sample sphere check stays cheap. */
    queryCellSize(): number {
        return this.voxelRes;
    }

    /** Get the metadata for external use (e.g., debug visualization). Bounds are in file-local space. */
    getMetadata(): VoxelOctreeMetadata {
        if (!this.flipXY) return this.meta;
        const flipBounds = (b: { min: number[]; max: number[] }) => ({
            min: [-b.max[0], -b.max[1], b.min[2]],
            max: [-b.min[0], -b.min[1], b.max[2]],
        });
        return {
            ...this.meta,
            gridBounds: flipBounds(this.meta.gridBounds),
            sceneBounds: flipBounds(this.meta.sceneBounds),
        };
    }

    // ── Point query ──────────────────────────────────────────────────────

    /** Test whether file-local position (wx, wy, wz) is inside a solid voxel. */
    checkPointSolid(wx: number, wy: number, wz: number): boolean {
        if (this.flipXY) { wx = -wx; wy = -wy; }
        if (wx < this.gridMinX || wx >= this.gridMaxX ||
            wy < this.gridMinY || wy >= this.gridMaxY ||
            wz < this.gridMinZ || wz >= this.gridMaxZ) {
            return false;
        }
        if (this.nodes.length === 0) return false;
        return this._descendPoint(0, wx, wy, wz,
            this.gridMinX, this.gridMinY, this.gridMinZ,
            this.gridMaxX, this.gridMaxY, this.gridMaxZ);
    }

    private _descendPoint(
        nodeIdx: number,
        px: number, py: number, pz: number,
        nMinX: number, nMinY: number, nMinZ: number,
        nMaxX: number, nMaxY: number, nMaxZ: number
    ): boolean {
        const node = this.nodes[nodeIdx];

        if ((node | 0) === (SOLID_LEAF_MARKER | 0)) return true;

        const childMask = (node >>> 24) & 0xFF;
        const baseOffset = node & 0xFFFFFF;

        // Mixed leaf (childMask=0) — test individual voxel bit
        if (childMask === 0) {
            const lx = Math.max(0, Math.min(3, Math.floor((px - nMinX) / this.voxelRes)));
            const ly = Math.max(0, Math.min(3, Math.floor((py - nMinY) / this.voxelRes)));
            const lz = Math.max(0, Math.min(3, Math.floor((pz - nMinZ) / this.voxelRes)));
            return testLeafBit(this.leafData, baseOffset, leafBitIndex(lx, ly, lz));
        }

        // Interior node — descend into the containing octant
        const cx = (nMinX + nMaxX) * 0.5;
        const cy = (nMinY + nMaxY) * 0.5;
        const cz = (nMinZ + nMaxZ) * 0.5;
        const octant = (px >= cx ? 1 : 0) | (py >= cy ? 2 : 0) | (pz >= cz ? 4 : 0);

        if (!((childMask >> octant) & 1)) return false;

        const childIdx = baseOffset + childIndexBefore(childMask, octant);
        return this._descendPoint(childIdx, px, py, pz,
            octant & 1 ? cx : nMinX, octant & 2 ? cy : nMinY, octant & 4 ? cz : nMinZ,
            octant & 1 ? nMaxX : cx, octant & 2 ? nMaxY : cy, octant & 4 ? nMaxZ : cz);
    }

    // ── Sphere query ─────────────────────────────────────────────────────

    /** Test whether a sphere (file-local center) intersects any solid voxel. */
    checkSphereSolid(wx: number, wy: number, wz: number, radius: number): boolean {
        if (this.flipXY) { wx = -wx; wy = -wy; }
        if (wx + radius < this.gridMinX || wx - radius >= this.gridMaxX ||
            wy + radius < this.gridMinY || wy - radius >= this.gridMaxY ||
            wz + radius < this.gridMinZ || wz - radius >= this.gridMaxZ) {
            return false;
        }
        if (this.nodes.length === 0) return false;
        return this._descendSphere(0, wx, wy, wz, radius * radius,
            this.gridMinX, this.gridMinY, this.gridMinZ,
            this.gridMaxX, this.gridMaxY, this.gridMaxZ);
    }

    private _descendSphere(
        nodeIdx: number,
        cx: number, cy: number, cz: number, r2: number,
        nMinX: number, nMinY: number, nMinZ: number,
        nMaxX: number, nMaxY: number, nMaxZ: number
    ): boolean {
        const node = this.nodes[nodeIdx];

        if ((node | 0) === (SOLID_LEAF_MARKER | 0)) return true;

        const childMask = (node >>> 24) & 0xFF;
        const baseOffset = node & 0xFFFFFF;

        // Mixed leaf — test each solid voxel's AABB against the sphere
        if (childMask === 0) {
            const originX = nMinX;
            const originY = nMinY;
            const originZ = nMinZ;
            const res = this.voxelRes;

            for (let lz = 0; lz < 4; lz++) {
                for (let ly = 0; ly < 4; ly++) {
                    for (let lx = 0; lx < 4; lx++) {
                        if (!testLeafBit(this.leafData, baseOffset, leafBitIndex(lx, ly, lz))) continue;
                        // Voxel AABB
                        const vMinX = originX + lx * res;
                        const vMinY = originY + ly * res;
                        const vMinZ = originZ + lz * res;
                        const vMaxX = vMinX + res;
                        const vMaxY = vMinY + res;
                        const vMaxZ = vMinZ + res;
                        // Closest point on AABB to sphere center
                        const ex = Math.max(vMinX - cx, 0, cx - vMaxX);
                        const ey = Math.max(vMinY - cy, 0, cy - vMaxY);
                        const ez = Math.max(vMinZ - cz, 0, cz - vMaxZ);
                        if (ex * ex + ey * ey + ez * ez <= r2) return true;
                    }
                }
            }
            return false;
        }

        // Interior node — test all occupied children whose AABB overlaps sphere
        const midX = (nMinX + nMaxX) * 0.5;
        const midY = (nMinY + nMaxY) * 0.5;
        const midZ = (nMinZ + nMaxZ) * 0.5;

        for (let oct = 0; oct < 8; oct++) {
            if (!((childMask >> oct) & 1)) continue;
            const cMinX = oct & 1 ? midX : nMinX;
            const cMinY = oct & 2 ? midY : nMinY;
            const cMinZ = oct & 4 ? midZ : nMinZ;
            const cMaxX = oct & 1 ? nMaxX : midX;
            const cMaxY = oct & 2 ? nMaxY : midY;
            const cMaxZ = oct & 4 ? nMaxZ : midZ;
            // AABB vs sphere overlap
            const ex = Math.max(cMinX - cx, 0, cx - cMaxX);
            const ey = Math.max(cMinY - cy, 0, cy - cMaxY);
            const ez = Math.max(cMinZ - cz, 0, cz - cMaxZ);
            if (ex * ex + ey * ey + ez * ez <= r2) {
                const childIdx = baseOffset + childIndexBefore(childMask, oct);
                if (this._descendSphere(childIdx, cx, cy, cz, r2,
                    cMinX, cMinY, cMinZ, cMaxX, cMaxY, cMaxZ)) {
                    return true;
                }
            }
        }
        return false;
    }

    // ── Ground query (vertical ray down) ─────────────────────────────────

    /**
     * Cast a vertical ray downward at (wx, wz) and return the Y of the highest
     * solid voxel top surface below maxY, or null if none found.
     */
    findGround(wx: number, wz: number, maxY?: number): number | null {
        if (this.flipXY) {
            // File-down is stored-up: a file top face at fy is the stored bottom
            // face at -fy, i.e. stored top at (voxelRes - fy). Highest file top
            // ≤ maxY ⇔ lowest stored top ≥ (voxelRes - maxY).
            const sx = -wx;
            if (sx < this.gridMinX || sx >= this.gridMaxX ||
                wz < this.gridMinZ || wz >= this.gridMaxZ) {
                return null;
            }
            if (this.nodes.length === 0) return null;
            const minStoredTopY = maxY === undefined ? -Infinity : this.voxelRes - maxY;
            const r = this._findCeiling(0, sx, wz, minStoredTopY,
                this.gridMinX, this.gridMinY, this.gridMinZ,
                this.gridMaxX, this.gridMaxY, this.gridMaxZ);
            return r === null ? null : this.voxelRes - r;
        }
        const upperY = maxY ?? this.gridMaxY;
        if (wx < this.gridMinX || wx >= this.gridMaxX ||
            wz < this.gridMinZ || wz >= this.gridMaxZ) {
            return null;
        }
        if (this.nodes.length === 0) return null;
        return this._findGround(0, wx, wz, upperY,
            this.gridMinX, this.gridMinY, this.gridMinZ,
            this.gridMaxX, this.gridMaxY, this.gridMaxZ);
    }

    /**
     * Cast a vertical ray for engines where world Y is inverted from file Y.
     * Returns the lowest solid voxel surface at or above minY in file space,
     * which maps to the highest reachable ground below the player in world space.
     */
    findGroundInvertedY(wx: number, wz: number, minY: number): number | null {
        if (this.flipXY) {
            // Callers use this when file-Y is inverted from world-Y. The
            // world-walkable face is the lowest file Y of the topmost-in-world
            // solid voxel — i.e. the stored top face negated. Lowest file face
            // ≥ minY ⇔ highest stored top ≤ -minY.
            const sx = -wx;
            if (sx < this.gridMinX || sx >= this.gridMaxX ||
                wz < this.gridMinZ || wz >= this.gridMaxZ) {
                return null;
            }
            if (this.nodes.length === 0) return null;
            const r = this._findGround(0, sx, wz, -minY,
                this.gridMinX, this.gridMinY, this.gridMinZ,
                this.gridMaxX, this.gridMaxY, this.gridMaxZ);
            return r === null ? null : -r;
        }
        if (wx < this.gridMinX || wx >= this.gridMaxX ||
            wz < this.gridMinZ || wz >= this.gridMaxZ) {
            return null;
        }
        if (this.nodes.length === 0) return null;
        return this._findCeiling(0, wx, wz, minY,
            this.gridMinX, this.gridMinY, this.gridMinZ,
            this.gridMaxX, this.gridMaxY, this.gridMaxZ);
    }

    private _findGround(
        nodeIdx: number,
        px: number, pz: number, maxY: number,
        nMinX: number, nMinY: number, nMinZ: number,
        nMaxX: number, nMaxY: number, nMaxZ: number
    ): number | null {
        if (nMinY >= maxY) return null;

        const node = this.nodes[nodeIdx];

        if ((node | 0) === (SOLID_LEAF_MARKER | 0)) {
            return Math.min(nMaxY, maxY);
        }

        const childMask = (node >>> 24) & 0xFF;
        const baseOffset = node & 0xFFFFFF;

        // Mixed leaf — scan the column at (px,pz) from top down
        if (childMask === 0) {
            const lx = Math.max(0, Math.min(3, Math.floor((px - nMinX) / this.voxelRes)));
            const lz = Math.max(0, Math.min(3, Math.floor((pz - nMinZ) / this.voxelRes)));

            for (let ly = 3; ly >= 0; ly--) {
                const voxelTopY = nMinY + (ly + 1) * this.voxelRes;
                if (voxelTopY > maxY) continue;
                if (testLeafBit(this.leafData, baseOffset, leafBitIndex(lx, ly, lz))) {
                    return voxelTopY;
                }
            }
            return null;
        }

        // Interior node — visit children in the (px,pz) column, Y-upper first
        const midX = (nMinX + nMaxX) * 0.5;
        const midY = (nMinY + nMaxY) * 0.5;
        const midZ = (nMinZ + nMaxZ) * 0.5;

        const xBit = px >= midX ? 1 : 0;
        const zBit = pz >= midZ ? 4 : 0;

        // Visit upper-Y octant first (y=1 → bit1 set), then lower-Y
        const upperOct = 2 | xBit | zBit;
        const lowerOct = 0 | xBit | zBit;

        for (const oct of [upperOct, lowerOct]) {
            if (!((childMask >> oct) & 1)) continue;

            const cMinX = oct & 1 ? midX : nMinX;
            const cMinY = oct & 2 ? midY : nMinY;
            const cMinZ = oct & 4 ? midZ : nMinZ;
            const cMaxX = oct & 1 ? nMaxX : midX;
            const cMaxY = oct & 2 ? nMaxY : midY;
            const cMaxZ = oct & 4 ? nMaxZ : midZ;

            const childIdx = baseOffset + childIndexBefore(childMask, oct);
            const result = this._findGround(childIdx, px, pz, maxY,
                cMinX, cMinY, cMinZ, cMaxX, cMaxY, cMaxZ);
            if (result !== null) return result;
        }
        return null;
    }

    // ── Inverted-Y ground query (for engines that negate Y from file space) ──

    /**
     * Find ground in world space when the engine negates Y from file space.
     * The splat entity has scale (1,-1,-1), so world→file: (x,-y,-z).
     *
     * Searches for the LOWEST solid voxel bottom surface ABOVE minFileY in file space,
     * which corresponds to the HIGHEST ground surface BELOW the player in world space.
     *
     * @param worldX  World X coordinate
     * @param worldZ  World Z coordinate (will be negated for file space)
     * @param worldY  World Y of player position
     * @param stepHeight  How far above feet to allow stepping onto
     * @returns World Y of ground surface, or null if no ground found
     */
    findGroundWorld(worldX: number, worldZ: number, worldY: number, stepHeight: number): number | null {
        // Transform to file space
        const fileX = worldX;
        const fileZ = -worldZ;
        // Player feet in file space: -(worldY - playerHeight) but we don't know playerHeight here,
        // so the caller passes worldY already adjusted. We search around -worldY in file space.
        const fileY = -worldY;

        // Bounds are checked by findGroundInvertedY, which also handles the
        // engine-space (flipXY) coordinate mapping for format 1.1+ data.

        // In file space, we want the lowest solid bottom Y above (fileY - stepHeight).
        // "Lowest" in file = "highest" in world, which is the ground we walk on.
        const minFileY = fileY - stepHeight;
        const result = this.findGroundInvertedY(fileX, fileZ, minFileY);

        // Transform result back to world space (negate Y)
        return result !== null ? -result : null;
    }

    /**
     * Find the lowest solid voxel TOP surface at or above minY in file space.
     * Searches Y-lower octants first (opposite of findGround).
     */
    private _findCeiling(
        nodeIdx: number,
        px: number, pz: number, minY: number,
        nMinX: number, nMinY: number, nMinZ: number,
        nMaxX: number, nMaxY: number, nMaxZ: number
    ): number | null {
        if (nMaxY < minY) return null;

        const node = this.nodes[nodeIdx];

        if ((node | 0) === (SOLID_LEAF_MARKER | 0)) {
            // Fully solid — return lowest valid Y (the top of the node if it starts below minY)
            return Math.max(nMinY, minY);
        }

        const childMask = (node >>> 24) & 0xFF;
        const baseOffset = node & 0xFFFFFF;

        // Mixed leaf — scan column at (px,pz) from bottom up
        if (childMask === 0) {
            const lx = Math.max(0, Math.min(3, Math.floor((px - nMinX) / this.voxelRes)));
            const lz = Math.max(0, Math.min(3, Math.floor((pz - nMinZ) / this.voxelRes)));

            for (let ly = 0; ly < 4; ly++) {
                const voxelTopY = nMinY + (ly + 1) * this.voxelRes;
                if (voxelTopY < minY) continue;
                if (testLeafBit(this.leafData, baseOffset, leafBitIndex(lx, ly, lz))) {
                    return voxelTopY;
                }
            }
            return null;
        }

        // Interior node — visit Y-lower octant first, then Y-upper
        const midX = (nMinX + nMaxX) * 0.5;
        const midY = (nMinY + nMaxY) * 0.5;
        const midZ = (nMinZ + nMaxZ) * 0.5;

        const xBit = px >= midX ? 1 : 0;
        const zBit = pz >= midZ ? 4 : 0;

        const lowerOct = 0 | xBit | zBit;
        const upperOct = 2 | xBit | zBit;

        for (const oct of [lowerOct, upperOct]) {
            if (!((childMask >> oct) & 1)) continue;

            const cMinX = oct & 1 ? midX : nMinX;
            const cMinY = oct & 2 ? midY : nMinY;
            const cMinZ = oct & 4 ? midZ : nMinZ;
            const cMaxX = oct & 1 ? nMaxX : midX;
            const cMaxY = oct & 2 ? nMaxY : midY;
            const cMaxZ = oct & 4 ? nMaxZ : midZ;

            const childIdx = baseOffset + childIndexBefore(childMask, oct);
            const result = this._findCeiling(childIdx, px, pz, minY,
                cMinX, cMinY, cMinZ, cMaxX, cMaxY, cMaxZ);
            if (result !== null) return result;
        }
        return null;
    }

    // ── Iterative point query (no recursion, for hot-path use) ─────────

    /**
     * Test whether the voxel whose minimum corner is (ix, iy, iz) (file-local units, i.e. a
     * multiple of voxelResolution from the grid minimum) is solid.
     * Uses iterative tree descent with bit-shifting — faster than recursive AABB
     * descent for repeated point queries (e.g. scanning a sphere's bounding box).
     */
    isVoxelSolid(ix: number, iy: number, iz: number): boolean {
        if (this.flipXY) {
            // Cell-min mapping, not point negation: the file-space cell
            // [ix, ix+res) occupies stored [-ix-res, -ix), whose min is -ix-res.
            return this._isVoxelSolidStored(-ix - this.voxelRes, -iy - this.voxelRes, iz);
        }
        return this._isVoxelSolidStored(ix, iy, iz);
    }

    /** Stored-space implementation of isVoxelSolid (no flip). (ix, iy, iz) is a cell's minimum corner. */
    private _isVoxelSolidStored(ix: number, iy: number, iz: number): boolean {
        // Cell minimums sit on gridMin + k·voxelRes; rounding absorbs float error in k.
        const res = this.voxelRes;
        return this._isCellSolid(
            Math.round((ix - this.gridMinX) / res),
            Math.round((iy - this.gridMinY) / res),
            Math.round((iz - this.gridMinZ) / res),
        );
    }

    /**
     * Solid test by integer CELL INDEX (0-based from the padded octree minimum). The octree spans
     * leafSize·2^treeDepth cells per axis — a power of two — so each level's octant is one bit of
     * the index. Indexing by cell (not by coordinate) is what keeps this correct at fractional voxel
     * resolutions such as the 0.05 default: bit tests on raw coordinates only worked when the
     * resolution was a whole number.
     */
    private _isCellSolid(gx: number, gy: number, gz: number): boolean {
        const cells = this.cellsPerAxis;
        if (gx < 0 || gy < 0 || gz < 0 || gx >= cells || gy >= cells || gz >= cells) return false;
        if (this.nodes.length === 0) return false;

        let nodeIdx = 0;
        let size = cells;
        while (true) {
            const node = this.nodes[nodeIdx];
            if ((node | 0) === (SOLID_LEAF_MARKER | 0)) return true;

            const childMask = (node >>> 24) & 0xFF;
            const baseOffset = node & 0xFFFFFF;

            // Leaf node (4x4x4 block, aligned to a multiple of leafSize cells)
            if (childMask === 0) {
                const ls = this.meta.leafSize;
                return testLeafBit(this.leafData, baseOffset, leafBitIndex(gx % ls, gy % ls, gz % ls));
            }

            // Interior: the octant is the next bit of each cell index
            size /= 2;
            const octant = ((gx & size) ? 1 : 0) | ((gy & size) ? 2 : 0) | ((gz & size) ? 4 : 0);
            if (!((childMask >> octant) & 1)) return false;

            nodeIdx = baseOffset + childIndexBefore(childMask, octant);
        }
    }

    // ── Sphere push-out query ────────────────────────────────────────────

    /**
     * Query a sphere against the voxel grid and return the push-out displacement
     * needed to resolve penetration, or null if no collision.
     *
     * Algorithm (matching SuperSplat's voxel-collider):
     * 1. Convert sphere center to voxel grid integer coords
     * 2. Scan bounding box of voxels around the sphere
     * 3. For each solid voxel, compute sphere-vs-AABB penetration
     * 4. Find the deepest penetrating voxel
     * 5. Push out along axis of shallowest penetration for that voxel
     * 6. Repeat up to 4 iterations (handles corners)
     * 7. Track constraint normals to prevent oscillation
     */
    querySphere(cx: number, cy: number, cz: number, radius: number): PushOut | null {
        if (this.flipXY) {
            // Run the whole iteration in stored space, then flip the push-out back.
            const stored = this._querySphereStored(-cx, -cy, cz, radius);
            return stored === null ? null : { x: -stored.x, y: -stored.y, z: stored.z };
        }
        return this._querySphereStored(cx, cy, cz, radius);
    }

    private _querySphereStored(cx: number, cy: number, cz: number, radius: number): PushOut | null {
        const res = this.voxelRes;
        if (this.nodes.length === 0 || !(radius > 0)) return null;
        // Cheap octree-pruned early out: most frames the sphere touches nothing, so skip the
        // per-cell scan below entirely.
        if (cx + radius < this.gridMinX || cx - radius >= this.gridMaxX ||
            cy + radius < this.gridMinY || cy - radius >= this.gridMaxY ||
            cz + radius < this.gridMinZ || cz - radius >= this.gridMaxZ ||
            !this._descendSphere(0, cx, cy, cz, radius * radius,
                this.gridMinX, this.gridMinY, this.gridMinZ,
                this.gridMaxX, this.gridMaxY, this.gridMaxZ)) {
            return null;
        }
        const gMinX = this.gridMinX, gMinY = this.gridMinY, gMinZ = this.gridMinZ;

        let px = cx;
        let py = cy;
        let pz = cz;

        let totalX = 0;
        let totalY = 0;
        let totalZ = 0;
        let hadCollision = false;

        // Constraint normals to prevent oscillation
        let cnX = 0, cnY = 0, cnZ = 0;
        let numConstraints = 0;

        for (let iter = 0; iter < 4; iter++) {
            // Cell-index bounding box around the sphere, on the octree's own grid (cells start at
            // gridMin, not at the origin). Out-of-grid cells are rejected by _isCellSolid.
            const minKX = Math.floor((px - radius - gMinX) / res);
            const minKY = Math.floor((py - radius - gMinY) / res);
            const minKZ = Math.floor((pz - radius - gMinZ) / res);
            const maxKX = Math.floor((px + radius - gMinX) / res);
            const maxKY = Math.floor((py + radius - gMinY) / res);
            const maxKZ = Math.floor((pz + radius - gMinZ) / res);

            let bestDepth = -Infinity;
            let bestPushX = 0, bestPushY = 0, bestPushZ = 0;

            for (let kz = minKZ; kz <= maxKZ; kz++) {
                for (let ky = minKY; ky <= maxKY; ky++) {
                    for (let kx = minKX; kx <= maxKX; kx++) {
                        // Voxel AABB
                        const vMinX = gMinX + kx * res;
                        const vMinY = gMinY + ky * res;
                        const vMinZ = gMinZ + kz * res;
                        const vMaxX = vMinX + res;
                        const vMaxY = vMinY + res;
                        const vMaxZ = vMinZ + res;

                        // Closest point on AABB to sphere center
                        const cpx = Math.max(vMinX, Math.min(px, vMaxX));
                        const cpy = Math.max(vMinY, Math.min(py, vMaxY));
                        const cpz = Math.max(vMinZ, Math.min(pz, vMaxZ));

                        const dx = px - cpx;
                        const dy = py - cpy;
                        const dz = pz - cpz;
                        const dist2 = dx * dx + dy * dy + dz * dz;

                        // Out of reach — skip before the (costlier) octree lookup.
                        if (dist2 >= radius * radius) continue;
                        if (!this._isCellSolid(kx, ky, kz)) continue;

                        // Compute penetration along each axis
                        const overlapX = (vMaxX - vMinX) / 2 + radius - Math.abs(px - (vMinX + vMaxX) / 2);
                        const overlapY = (vMaxY - vMinY) / 2 + radius - Math.abs(py - (vMinY + vMaxY) / 2);
                        const overlapZ = (vMaxZ - vMinZ) / 2 + radius - Math.abs(pz - (vMinZ + vMaxZ) / 2);

                        if (overlapX <= 0 || overlapY <= 0 || overlapZ <= 0) continue;

                        // Depth = distance from center to closest point (negative = inside)
                        const depth = radius - Math.sqrt(dist2);

                        if (depth > bestDepth) {
                            bestDepth = depth;

                            // Push out along axis of shallowest penetration
                            if (overlapX <= overlapY && overlapX <= overlapZ) {
                                bestPushX = Math.sign(px - (vMinX + vMaxX) / 2) * overlapX;
                                bestPushY = 0;
                                bestPushZ = 0;
                            } else if (overlapY <= overlapZ) {
                                bestPushX = 0;
                                bestPushY = Math.sign(py - (vMinY + vMaxY) / 2) * overlapY;
                                bestPushZ = 0;
                            } else {
                                bestPushX = 0;
                                bestPushY = 0;
                                bestPushZ = Math.sign(pz - (vMinZ + vMaxZ) / 2) * overlapZ;
                            }
                        }
                    }
                }
            }

            if (bestDepth <= 0) break;

            // Project push-out to remove component along previous constraint normals
            // This prevents oscillation when hitting corners
            if (numConstraints > 0) {
                const dot = bestPushX * cnX + bestPushY * cnY + bestPushZ * cnZ;
                if (dot < 0) {
                    bestPushX -= dot * cnX;
                    bestPushY -= dot * cnY;
                    bestPushZ -= dot * cnZ;
                }
            }

            // Apply push-out
            px += bestPushX;
            py += bestPushY;
            pz += bestPushZ;
            totalX += bestPushX;
            totalY += bestPushY;
            totalZ += bestPushZ;
            hadCollision = true;

            // Track constraint normal
            const pushLen = Math.sqrt(bestPushX * bestPushX + bestPushY * bestPushY + bestPushZ * bestPushZ);
            if (pushLen > 1e-6) {
                cnX = bestPushX / pushLen;
                cnY = bestPushY / pushLen;
                cnZ = bestPushZ / pushLen;
                numConstraints++;
            }
        }

        if (!hadCollision) return null;
        return { x: totalX, y: totalY, z: totalZ };
    }

    // ── Debug visualization helpers ──────────────────────────────────────

    /**
     * Collect AABBs for all solid leaf-level voxels.
     * Returns Float32Array of [cx, cy, cz, halfExtent, ...] quads.
     * For large octrees this can be expensive — use maxLeaves to cap output.
     */
    collectSolidAABBs(maxLeaves: number = 200_000): Float32Array {
        const result: number[] = [];
        this._collectSolid(0,
            this.gridMinX, this.gridMinY, this.gridMinZ,
            this.gridMaxX, this.gridMaxY, this.gridMaxZ,
            result, maxLeaves);
        return this._aabbsToFileSpace(new Float32Array(result));
    }

    /** Flip [cx,cy,cz,half] quads from stored (engine) space to file-local space. */
    private _aabbsToFileSpace(quads: Float32Array): Float32Array {
        if (!this.flipXY) return quads;
        for (let i = 0; i < quads.length; i += 4) {
            quads[i] = -quads[i];
            quads[i + 1] = -quads[i + 1];
        }
        return quads;
    }

    /**
     * Collect AABBs at the leaf level — one box per leaf node (both solid and mixed).
     * Unlike collectSolidAABBs, mixed leaves are NOT expanded to individual voxels,
     * so the output covers the full scene much more uniformly within a given cap.
     * Returns Float32Array of [cx, cy, cz, halfExtent, ...] quads.
     */
    collectLeafAABBs(maxLeaves: number = 500_000): Float32Array {
        // Diagnostic: log root node info to debug coverage issues
        if (this.nodes.length > 0) {
            const root = this.nodes[0];
            const rootMask = (root >>> 24) & 0xFF;
            const rootBase = root & 0xFFFFFF;
            const midX = (this.gridMinX + this.gridMaxX) * 0.5;
            const midY = (this.gridMinY + this.gridMaxY) * 0.5;
            const midZ = (this.gridMinZ + this.gridMaxZ) * 0.5;
            const octLabels = ['---','X--','-Y-','XY-','--Z','X-Z','-YZ','XYZ'];
            const active = [];
            for (let i = 0; i < 8; i++) if (rootMask & (1 << i)) active.push(`${i}(${octLabels[i]})`);
            console.log(`[VoxelCollision] Root: mask=0b${rootMask.toString(2).padStart(8,'0')} base=${rootBase} mid=(${midX.toFixed(1)},${midY.toFixed(1)},${midZ.toFixed(1)}) active=[${active.join(',')}]`);
        }
        const result: number[] = [];
        this._collectLeaves(0,
            this.gridMinX, this.gridMinY, this.gridMinZ,
            this.gridMaxX, this.gridMaxY, this.gridMaxZ,
            result, maxLeaves);
        return this._aabbsToFileSpace(new Float32Array(result));
    }

    private _collectLeaves(
        nodeIdx: number,
        nMinX: number, nMinY: number, nMinZ: number,
        nMaxX: number, nMaxY: number, nMaxZ: number,
        out: number[], maxLeaves: number
    ): void {
        if (out.length / 4 >= maxLeaves) return;

        const node = this.nodes[nodeIdx];

        // Fully solid — emit as a single box
        if ((node | 0) === (SOLID_LEAF_MARKER | 0)) {
            out.push((nMinX + nMaxX) * 0.5, (nMinY + nMaxY) * 0.5, (nMinZ + nMaxZ) * 0.5, (nMaxX - nMinX) * 0.5);
            return;
        }

        const childMask = (node >>> 24) & 0xFF;

        // Mixed leaf — emit the whole 4×4×4 block as one box (don't expand individual voxels)
        if (childMask === 0) {
            out.push((nMinX + nMaxX) * 0.5, (nMinY + nMaxY) * 0.5, (nMinZ + nMaxZ) * 0.5, (nMaxX - nMinX) * 0.5);
            return;
        }

        // Interior node — recurse children
        const midX = (nMinX + nMaxX) * 0.5;
        const midY = (nMinY + nMaxY) * 0.5;
        const midZ = (nMinZ + nMaxZ) * 0.5;

        for (let oct = 0; oct < 8; oct++) {
            if (!((childMask >> oct) & 1)) continue;
            if (out.length / 4 >= maxLeaves) return;
            const cMinX = oct & 1 ? midX : nMinX;
            const cMinY = oct & 2 ? midY : nMinY;
            const cMinZ = oct & 4 ? midZ : nMinZ;
            const cMaxX = oct & 1 ? nMaxX : midX;
            const cMaxY = oct & 2 ? nMaxY : midY;
            const cMaxZ = oct & 4 ? nMaxZ : midZ;

            const childIdx = (node & 0xFFFFFF) + childIndexBefore(childMask, oct);
            this._collectLeaves(childIdx,
                cMinX, cMinY, cMinZ, cMaxX, cMaxY, cMaxZ,
                out, maxLeaves);
        }
    }

    private _collectSolid(
        nodeIdx: number,
        nMinX: number, nMinY: number, nMinZ: number,
        nMaxX: number, nMaxY: number, nMaxZ: number,
        out: number[], maxLeaves: number
    ): void {
        if (out.length / 4 >= maxLeaves) return;

        const node = this.nodes[nodeIdx];

        // Fully solid — emit as a single box
        if ((node | 0) === (SOLID_LEAF_MARKER | 0)) {
            const cx = (nMinX + nMaxX) * 0.5;
            const cy = (nMinY + nMaxY) * 0.5;
            const cz = (nMinZ + nMaxZ) * 0.5;
            const half = (nMaxX - nMinX) * 0.5;
            out.push(cx, cy, cz, half);
            return;
        }

        const childMask = (node >>> 24) & 0xFF;
        const baseOffset = node & 0xFFFFFF;

        // Mixed leaf — emit individual solid voxels
        if (childMask === 0) {
            const res = this.voxelRes;
            const halfRes = res * 0.5;
            for (let lz = 0; lz < 4; lz++) {
                for (let ly = 0; ly < 4; ly++) {
                    for (let lx = 0; lx < 4; lx++) {
                        if (out.length / 4 >= maxLeaves) return;
                        if (!testLeafBit(this.leafData, baseOffset, leafBitIndex(lx, ly, lz))) continue;
                        out.push(
                            nMinX + lx * res + halfRes,
                            nMinY + ly * res + halfRes,
                            nMinZ + lz * res + halfRes,
                            halfRes
                        );
                    }
                }
            }
            return;
        }

        // Interior node — recurse children
        const midX = (nMinX + nMaxX) * 0.5;
        const midY = (nMinY + nMaxY) * 0.5;
        const midZ = (nMinZ + nMaxZ) * 0.5;

        for (let oct = 0; oct < 8; oct++) {
            if (!((childMask >> oct) & 1)) continue;
            if (out.length / 4 >= maxLeaves) return;
            const cMinX = oct & 1 ? midX : nMinX;
            const cMinY = oct & 2 ? midY : nMinY;
            const cMinZ = oct & 4 ? midZ : nMinZ;
            const cMaxX = oct & 1 ? nMaxX : midX;
            const cMaxY = oct & 2 ? nMaxY : midY;
            const cMaxZ = oct & 4 ? nMaxZ : midZ;

            const childIdx = baseOffset + childIndexBefore(childMask, oct);
            this._collectSolid(childIdx,
                cMinX, cMinY, cMinZ, cMaxX, cMaxY, cMaxZ,
                out, maxLeaves);
        }
    }
}
